import { CSSProperties } from 'react';
import { ModalProps as RBModalProps } from 'react-bootstrap';

export type AlignmentType = 'left' | 'center' | 'right';
export type ModalType = 'fit' | 'wide-padding';

export enum ModalSizes {
  LG = '50rem',
  MD = '35rem',
  SM = '22.5rem',
  XL = '65.625rem',
  XSM = '18rem',
}
export interface IDynamicResizing {
  margin?: string | number;
  maxWidth?: string | number;
  minWidth?: string | number;
}

export type ModalSizeType = 'sm' | 'md' | 'lg' | 'xl';

export interface IModalSizeProps {
  default: ModalSizeType;
  mobileMode: 'msx' | 'fs';
}

export interface IHaiModalProps extends Omit<RBModalProps, 'size'> {
  /**
   * Add an optional extra class name to .modal-backdrop, from react-bootstrap component
   */
  backdropClassName?: string;
  /**
   * If true, the modal will be centered in the page. Otherwise, it is placed near the top of the screen.
   * Default value is true.
   *
   * @default true
   */
  centered?: boolean;

  /**
   * Support any type of children suported by a react bootstrap Modal
   */
  children?: any;

  /**
   * CSS class to apply to the Modal container DOM node, from react-bootstrap component
   */
  className?: string;

  /**
   * CSS class to apply to the Modal dialog DOM node, from react-bootstrap component
   */
  dialogClassName?: string;

  /**
   * Creates a responsive Modal with the desired dimensions and margin between the Modal and window horizontally.
   * It receives an object with the following attributes, where each attribute is optional and has a default.
   * A modal receives its own max width as the maxWidth and the maxWidth of the smaller modal as it's minWidth by default.
   * The default margin is 6.25rem.
   * e.g. md Modal maxWidth = 35rem, minWidth = 22.5rem
   *
   * {
   * minWidth?: string | number;
   * maxWidth?: string | number;
   * margin?: string | number;
   * }
   * or it can receive a boolean 'true' to pass in all the default values.
   * Receiving 'false' will render a fixed-sized modal.
   *
   * @default false
   *
   */
  dynamicResizing?: IDynamicResizing | boolean;

  /**
   * Function that runs when the backdrop is triggered
   *
   * @default () => void
   */
  onHide?(): void;

  /**
   * When true the modal will show itself, from react-bootstrap component
   */
  show?: boolean;

  /**
   * The size may be a regular small, medium (default), large, or extra-large modal, from the react-bootstrap component
   * or an object (IModalSizeProps) '{default:'sm' | 'md' | 'lg' | 'xl' (ModalSizeType), mobileMode: 'fs'|'msx'}'.
   * The custom object can be used to custom the mobile sizing of the modal. 'fs' is full-screen, 'msx' is mobile small.
   * If a regular size (ModalSizeType) is passed in ('sm' | 'md' | 'lg' | 'xl') lg and xl sizes will be mapped to fs, whereas sm and md will become msx
   */
  size?: ModalSizeType | IModalSizeProps;

  /**
   * Applies custom styling to the component
   */
  style?: CSSProperties;

  /**
   * Specify a type for the Modal
   * Currently only type support is 'fit'
   * type fit adds .modal-fit class
   */
  type?: ModalType;

  /**
   * Specifies a custom z-index for the component
   */
  zindex?: number;
}

export interface IHaiModalHeader {
  /**
   * specify the header text position
   *
   * AlignmentType = 'left' | 'center' | 'right';
   *
   * @default 'left'
   */
  alignment?: AlignmentType;

  /**
   * Any components/elements as modal header content
   */
  children?: any;
  /**
   * CSS class to apply to the Modal container DOM node, from react-bootstrap component
   */
  className?: string;

  /**
   * show or hide the close button 'X'
   *
   * @default false
   */
  closeButton?: boolean;

  /**
   * Function that runs when the backdrop is triggered
   *
   * @default () => void
   */
  onHide?(): void;

  /**
   *  the value of ThemeContext
   */
  theme?: string;
}

export interface IHaiModalBody {
  /**
   * Any components/elements as modal body content
   */
  children?: any;

  /**
   * CSS class to apply to the Modal container DOM node, from react-bootstrap component
   */
  className?: string;

  /**
   * specify the selected ModalBodyItem
   */
  selectedContent?: string;

  /**
   * for custom stylings for the ModalBody
   */
  style?: CSSProperties;
}

export interface IHaiModalBodyItem {
  /**
   * Any components/elements as modal body content
   */
  children?: any;

  /**
   *
   * CSS class to apply to the Modal container DOM node, from react-bootstrap component
   */
  className?: string;

  /**
   * Identify the item for selection
   */
  eventkey: string;
}

export interface IHaiModalFooter {
  /**
   * Any components/elements as modal footer content
   */
  children?: any;

  /**
   * CSS class to apply to the Modal container DOM node, from react-bootstrap component
   */
  className?: string;
  /**
   * Alignment of the footer in full-screen mobile modals.
   * Absolute always pushes the footer to the bottom of the modal.
   * Relative aligns it underneath the rest of the content
   *
   * @default 'absolute'
   */
  mobileAlignment?: 'absolute' | 'relative';
}
